import React, { useCallback, useMemo } from 'react'
import { closeModal, GLOBAL_MODAL_TYPE, selectIsModalOpen, selectModalActionProps } from 'state/constants'
import { useDispatch, useSelector } from 'react-redux'
import ModalStyledPopup from 'components/ModalStyledPopup'
import { Flex, Box, Text } from 'rebass'
import styled from 'particles/themes/styled'
import { theme } from 'theme'
import { useNewTranslation } from 'hooks/index'
import ButtonFa from 'components/ButtonFa'
import { faTimes } from '@fortawesome/free-solid-svg-icons'
import { SubmissionTags } from 'components/Tag'
import { parseResultData } from '../SimpleTable/RegistrationResultsTableDef'
import { selectRegistrationForm } from 'state/registrations'
import { BLOCK_TYPE } from 'state/registrations'
import { useStyles } from 'components/Shared/MuiTable/Cell/TagCell'
import MediaViewer, { MediaData } from 'components/MediaViewer'
import { Dialog } from '@mui/material'
import { parseBackendDate } from 'services/dates'
import { PLATFORM_REDIRECT_URL } from 'services/numbers'
import { PLATFORM_ENUM } from 'services/api/constants'
import { usePermission } from 'hooks/permissions'
import { CampaignSubmission } from 'components/Shared/Permission/enums'
import { selectSelectedCampaignSummary } from 'state/review'

const PopupContainer = styled(Box)`
    overflow: hidden;
    .close__button {
        position: absolute;
        right: 0;
        top: 0;
    }
`

const ActionModal: React.FC = () => {
    const [newT] = useNewTranslation()
    const dispatch = useDispatch()
    const [open, setOpen] = React.useState(false)
    const classes = useStyles()
    const isModalOpen = useSelector(selectIsModalOpen(GLOBAL_MODAL_TYPE.INTEREST_REGISTRATION_ACTION_DETAIL))
    const onCloseModal = useCallback(() => dispatch(closeModal()), [])
    const registrationForm = useSelector(selectRegistrationForm)
    const selectedCampaignSummary = useSelector(selectSelectedCampaignSummary)
    const actionProps = useSelector(selectModalActionProps)
    const canManageTag = usePermission(CampaignSubmission.ManageTag)
    const dynamicCols = useMemo(() => {
        if (registrationForm && registrationForm.blocks) {
            return registrationForm.blocks
                .filter(block => ![BLOCK_TYPE.TEXT, BLOCK_TYPE.PHOTOS].includes(block.type))
                .map(block => {
                    const field = `${block.type}+${block.uuid}`
                    const headerName = block.title
                    switch (block.type as Exclude<BLOCK_TYPE, BLOCK_TYPE.TEXT | BLOCK_TYPE.PHOTOS>) {
                        case BLOCK_TYPE.QUESTION:
                        case BLOCK_TYPE.TEXT_SUBMISSION:
                        case BLOCK_TYPE.NUMBER_SUBMISSION:
                        case BLOCK_TYPE.URL_SUBMISSION:
                        case BLOCK_TYPE.PHOTO_SUBMISSION:
                            return {
                                field,
                                headerName,
                                blockType: block.type,
                            }
                    }
                })
        } else {
            return []
        }
    }, [registrationForm])
    const resultsData =
        actionProps && actionProps.registration && actionProps.registrationFormBlocksById
            ? actionProps.registration.results_by_block.reduce((acc, curr) => {
                  const targetBlock = actionProps.registrationFormBlocksById[curr.uuid]
                  if (!targetBlock) return acc
                  return {
                      ...acc,
                      [`${targetBlock.type}+${curr.uuid}`]: parseResultData(
                          curr,
                          actionProps.registrationFormBlocksById,
                      ),
                  }
              }, {})
            : {}

    const handleClickImageBlock = (): void => {
        if (!open) setOpen(true)
    }

    return (
        <ModalStyledPopup open={isModalOpen} width={'412px'} closeOnDocumentClick={false}>
            <PopupContainer m={theme.spacing.s} height={'100%'}>
                <ButtonFa
                    icon={faTimes}
                    iconSize={'sm'}
                    iconColor={'gray'}
                    onClick={onCloseModal}
                    className={'close__button'}
                />
                <Text color={theme.colors.black} fontSize={'18px'} textAlign={'left'} mb={theme.spacing.xs}>
                    {newT('modalWindow.userDetailsTitle')}
                </Text>
                <Flex>
                    <Box width={1 / 2}>
                        <Box mb={'16px'}>
                            <Text color={'silverChalice'} fontSize={'14px'} textAlign={'left'} mb={'4px'}>
                                {newT('submission.table.nameLabel', 'Name')}
                            </Text>
                            <Text color={theme.colors.mineShaft} fontSize={'14px'} textAlign={'left'}>
                                {actionProps ? actionProps.profile.name : ''}
                            </Text>
                        </Box>
                        <Box mb={'16px'}>
                            <Text color={'silverChalice'} fontSize={'14px'} textAlign={'left'} mb={'4px'}>
                                {newT('submission.table.instagramUsernameLabel', 'IG username')}
                            </Text>
                            <Box mb={'4px'}>
                                <a
                                    href={
                                        actionProps && actionProps.igUserName
                                            ? PLATFORM_REDIRECT_URL[PLATFORM_ENUM.IG] +
                                              actionProps.igUserName.toString()
                                            : undefined
                                    }
                                    target={'popup'}
                                    style={{
                                        wordBreak: 'break-all',
                                        fontSize: '14px',
                                    }}
                                >
                                    {actionProps && actionProps.igUserName ? actionProps.igUserName.toString() : ''}
                                </a>
                            </Box>
                        </Box>
                        {actionProps && actionProps.hasFbTask && (
                            <Box mb={'16px'}>
                                <Text color={'silverChalice'} fontSize={'14px'} textAlign={'left'} mb={'4px'}>
                                    {newT('submission.table.facebookUsernameLabel', 'FB username')}
                                </Text>
                                <Box mb={'4px'}>
                                    <a
                                        href={
                                            actionProps && actionProps.fbUserName
                                                ? 'https://www.facebook.com/' + actionProps.fbUserName.toString()
                                                : undefined
                                        }
                                        target={'popup'}
                                        style={{
                                            wordBreak: 'break-all',
                                            fontSize: '14px',
                                        }}
                                    >
                                        {actionProps && actionProps.fbUserName ? actionProps.fbUserName.toString() : ''}
                                    </a>
                                </Box>
                            </Box>
                        )}
                        <Flex>
                            <Box width={1 / 2}>
                                <Box>
                                    <Text color={'silverChalice'} fontSize={'14px'} textAlign={'left'} mb={'4px'}>
                                        {newT('submission.table.genderLabel', 'Gender')}
                                    </Text>
                                    <Text color={theme.colors.mineShaft} fontSize={'14px'} textAlign={'left'}>
                                        {actionProps && actionProps.profile
                                            ? actionProps.profile.gender.toString()
                                            : ''}
                                    </Text>
                                </Box>
                            </Box>
                            <Box width={1 / 2}>
                                <Box>
                                    <Text color={'silverChalice'} fontSize={'14px'} textAlign={'left'} mb={'4px'}>
                                        {newT('submission.table.ageLabel', 'Age')}
                                    </Text>
                                    <Text color={theme.colors.mineShaft} fontSize={'14px'} textAlign={'left'}>
                                        {actionProps && actionProps.profile ? actionProps.profile.age.toString() : ''}
                                    </Text>
                                </Box>
                            </Box>
                        </Flex>
                    </Box>
                    <Box width={1 / 2}>
                        <Box mb={'16px'}>
                            <Text color={'silverChalice'} fontSize={'14px'} textAlign={'left'} mb={'4px'}>
                                {newT('submission.table.joinedDateLabel', 'Joined at')}
                            </Text>
                            <Text color={theme.colors.mineShaft} fontSize={'14px'} textAlign={'left'}>
                                {actionProps && actionProps.registration && selectedCampaignSummary
                                    ? parseBackendDate(
                                          actionProps.registration.created_at,
                                          selectedCampaignSummary.country,
                                          { showTime: true },
                                      )
                                    : ''}
                            </Text>
                        </Box>
                        {dynamicCols.map(col => (
                            <Box key={col.field} mb={'16px'}>
                                <Text color={'silverChalice'} fontSize={'14px'} textAlign={'left'} mb={'4px'}>
                                    {col.headerName}
                                </Text>
                                {(col.blockType === BLOCK_TYPE.QUESTION ||
                                    col.blockType === BLOCK_TYPE.TEXT_SUBMISSION ||
                                    col.blockType === BLOCK_TYPE.NUMBER_SUBMISSION) && (
                                    <Text color={theme.colors.mineShaft} fontSize={'14px'} textAlign={'left'}>
                                        {resultsData && resultsData[col.field] ? resultsData[col.field].value : ''}
                                    </Text>
                                )}
                                {col.blockType === BLOCK_TYPE.URL_SUBMISSION && (
                                    <Box mb={'4px'}>
                                        <a
                                            href={
                                                resultsData && resultsData[col.field]
                                                    ? resultsData[col.field].toString()
                                                    : undefined
                                            }
                                            target={'popup'}
                                            style={{
                                                wordBreak: 'break-all',
                                                fontSize: '14px',
                                            }}
                                        >
                                            {resultsData && resultsData[col.field] ? resultsData[col.field] : ''}
                                        </a>
                                    </Box>
                                )}
                                {col.blockType === BLOCK_TYPE.PHOTO_SUBMISSION && (
                                    <Box mb={'4px'}>
                                        <div
                                            className={classes.root}
                                            onClick={handleClickImageBlock}
                                            style={{ margin: '10px 0' }}
                                        >
                                            <MediaViewer
                                                mediaData={
                                                    resultsData && resultsData[col.field]
                                                        ? (resultsData[col.field] as MediaData)
                                                        : ({} as MediaData)
                                                }
                                                showPopup={false}
                                                showArrowButtons={false}
                                                maxHeight={'90px'}
                                            />
                                            <Dialog open={open} onClose={(): void => setOpen(false)}>
                                                <MediaViewer
                                                    mediaData={
                                                        resultsData && resultsData[col.field]
                                                            ? (resultsData[col.field] as MediaData)
                                                            : ({} as MediaData)
                                                    }
                                                    showPopup={false}
                                                    maxHeight={'500px'}
                                                />
                                            </Dialog>
                                        </div>
                                    </Box>
                                )}
                            </Box>
                        ))}
                        {canManageTag && (
                            <Box>
                                <Text color={'silverChalice'} fontSize={'14px'} textAlign={'left'} mb={'4px'}>
                                    {newT('submission.table.partiposterTagsLabel', 'Partiposter Tags')}
                                </Text>
                                {actionProps && actionProps.registration && (
                                    <SubmissionTags
                                        profileData={actionProps.profile}
                                        submissionUuid={actionProps.registration.submission_uuid}
                                        campaignUuid={actionProps.campaignUuid}
                                        coloredTags
                                        ellipsis
                                    />
                                )}
                            </Box>
                        )}
                    </Box>
                </Flex>
            </PopupContainer>
        </ModalStyledPopup>
    )
}

export default ActionModal
